import { assertNever } from './common/TypeScriptUtility';

/**
 * This type represents optional values. A value of type `Maybe<T>` represents an optional value of type `T`. It either contains a value of
 * type `T` (represented as `Just<T>`), or it is empty (represented as `Nothing`).
 *
 * NOTE could define `Maybe<T>` as `Either<T, null>`.
 */
export type Maybe<T> = Just<T> | Nothing;

export interface Just<T> {
  readonly type: 'Just';
  readonly value: T;
}

export interface Nothing {
  readonly type: 'Nothing';
}

/**
 * Smart constructor for creating a just value.
 *
 * @param value - The just value.
 */
export function just<T>(value: T): Maybe<T> {
  return {
    type: 'Just',
    value
  };
}

/**
 * A value for representing nothing.
 */
export const nothing: Nothing = {
  type: 'Nothing'
};

/**
 * Higher-order helper function for maybe values.
 *
 * @param f - Function to apply to a just value.
 * @param t - Default value to return when applied to nothing.
 */
export function maybe<S, T>(f: (s: S) => T, t: T): (m: Maybe<S>) => T {
  return (m: Maybe<S>) => {
    switch (m.type) {
      case 'Just': return f(m.value);
      case 'Nothing': return t;
      default: return assertNever(m);
    }
  };
}

/**
 * Returns a just value or throws an exception if given nothing.
 *
 * @param m - Maybe value.
 * @throws Error if given nothing.
 */
export function fromJust<T>(m: Maybe<T>): T {
  switch (m.type) {
    case 'Just': return m.value;
    default: throw new Error('Nothing');
  }
}

/**
 * Returns a just value or a given default if applied to nothing.
 *
 * @param t - Default value.
 */
export function fromMaybe<T>(t: T): (m: Maybe<T>) => T {
  return maybe(v => v, t);
}

/**
 * Checks if a given maybe value is just.
 *
 * @param m - Maybe value to check.
 */
export function isJust<T>(m: Maybe<T>): boolean {
  return maybe(_ => true, false)(m);
}

/**
 * Checks if a given maybe value is nothing.
 *
 * @param m - Maybe value to check.
 */
export function isNothing<T>(m: Maybe<T>): boolean {
  return !isJust(m);
}
